import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiCheckCircle, FiCalendar, FiUser, FiScissors, FiDownload } from 'react-icons/fi';
import { appointmentsAPI } from '../../api';

export default function BookingConfirmation() {
  const { id: bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (bookingId) {
      appointmentsAPI.getById(bookingId)
        .then(r => setBooking(r.data.data || r.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [bookingId]);

  if (loading) return (
    <div className="min-h-screen bg-salon-cream flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-salon-primary" />
    </div>
  );

  return (
    <div className="min-h-screen bg-salon-cream flex items-center justify-center py-20 px-4">
      <motion.div initial={{ opacity:0, scale:.9 }} animate={{ opacity:1, scale:1 }}
        className="card max-w-lg w-full p-8 md:p-12 text-center">

        {/* Success Icon */}
        <motion.div initial={{ scale:0 }} animate={{ scale:1 }} transition={{ delay:.2, type:'spring' }}
          className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
          <FiCheckCircle className="text-green-500 text-5xl" />
        </motion.div>

        <h1 className="font-display text-3xl md:text-4xl text-salon-dark mb-3">Booking Confirmed!</h1>
        <p className="text-salon-muted mb-8">
          Thank you! Your appointment has been booked. A confirmation has been sent to your email.
        </p>

        {bookingId && (
          <div className="bg-salon-cream rounded-xl p-4 mb-8 border border-salon-cream-dark">
            <p className="text-sm text-salon-muted mb-1">Booking Reference</p>
            <p className="font-display text-2xl text-salon-primary font-bold">#{bookingId.toString().padStart(6, '0')}</p>
          </div>
        )}

        {/* Booking Details */}
        {booking && (
          <div className="bg-white rounded-2xl p-6 mb-8 text-left space-y-4 border border-salon-cream-dark">
            <h3 className="font-display text-lg text-salon-dark mb-4">Appointment Details</h3>
            {[
              { icon: FiCalendar, label:'Date & Time', value: `${booking.appointment_date} at ${booking.appointment_time}` },
              { icon: FiUser, label:'Stylist', value: booking.staff?.name || 'Any Available' },
              { icon: FiScissors, label:'Services', value: (booking.services || []).map(s => s.name).join(', ') },
            ].map(item => (
              <div key={item.label} className="flex gap-3">
                <div className="w-8 h-8 rounded-lg bg-salon-primary/10 flex items-center justify-center flex-shrink-0">
                  <item.icon className="text-salon-primary text-sm" />
                </div>
                <div>
                  <p className="text-xs text-salon-muted">{item.label}</p>
                  <p className="text-salon-dark font-medium text-sm">{item.value}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* What's Next */}
        <div className="bg-salon-primary/5 rounded-2xl p-5 mb-8 text-left">
          <h4 className="font-semibold text-salon-dark mb-3">What to expect next</h4>
          <ul className="space-y-2 text-sm text-salon-muted">
            <li className="flex gap-2"><span className="text-salon-primary">✓</span> Confirmation email sent to your inbox</li>
            <li className="flex gap-2"><span className="text-salon-primary">✓</span> SMS reminder 24 hours before your appointment</li>
            <li className="flex gap-2"><span className="text-salon-primary">✓</span> Arrive 5 minutes early for check-in</li>
          </ul>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Link to="/dashboard/appointments" className="btn-primary flex-1 text-center">View My Appointments</Link>
          <Link to="/" className="btn-secondary flex-1 text-center">Back to Home</Link>
        </div>

        {bookingId && (
          <button onClick={() => window.print()}
            className="mt-4 text-salon-primary text-sm hover:underline flex items-center gap-1 mx-auto">
            <FiDownload className="text-xs" /> Download Receipt
          </button>
        )}
      </motion.div>
    </div>
  );
}
